import {
  COLOR_SPECIALIST_ROLE,
  HAIR_STYLIST_TIER_ROLES,
  NAIL_CARE_ROLES,
  SKIN_WELLNESS_ROLES,
  staffRoleSelectValue,
} from '../../admin-shared/staffRoleOptions';

/** Minimum stylist tier required to perform a priced service (higher = more senior). */
const STYLIST_TIER_RANK = {
  stylist: 1,
  lead: 1,
  hair_and_beauty: 1,
  color_specialist: 2,
  senior_stylist: 2,
  style_director: 3,
};

const TIER_LABEL_PATTERNS = [
  { tier: 'style_director', re: /style\s*director/i },
  { tier: 'senior_stylist', re: /senior\s*stylist|sr\.?\s*stylist/i },
];

function catalogLookup(catalog, row) {
  const byId = new Map();
  for (const svc of catalog || []) {
    const id = String(svc?.id || '').trim();
    if (id) byId.set(id, svc);
    const base = String(svc?.id || '').split('::')[0];
    if (base && !byId.has(base)) byId.set(base, svc);
  }
  return (
    byId.get(String(row?.id || '')) ||
    byId.get(String(row?.base_service_id || '')) ||
    null
  );
}

function tierFromText(text) {
  const raw = String(text || '');
  for (const { tier, re } of TIER_LABEL_PATTERNS) {
    if (re.test(raw)) return tier;
  }
  if (/\bstylist\b/i.test(raw) && !/senior|director/i.test(raw)) {
    return 'stylist';
  }
  return null;
}

/**
 * Required minimum hair stylist tier from a selected line + catalog row.
 * @returns {'style_director'|'senior_stylist'|'stylist'|null}
 */
export function inferRequiredStylistTier(selectedLine, catalogRow) {
  const line = selectedLine || {};
  const cat = catalogRow || {};
  const name = `${line.name || ''} ${cat.name || ''}`;
  const optLabel = line.option_label || '';
  const optKey = String(line.option_key || '').toLowerCase();

  let tier = tierFromText(optLabel) || tierFromText(name);
  if (tier) return tier;

  const options = Array.isArray(cat.price_options) ? cat.price_options : [];
  const chosen =
    options.find((o) => String(o?.key || '').toLowerCase() === optKey) ||
    options.find((o) => String(o?.label || '') === optLabel);
  if (chosen?.axis === 'stylist_tier') {
    const k = String(chosen.key || '').toLowerCase();
    if (k in STYLIST_TIER_RANK) return k;
  }
  if (chosen?.label) {
    tier = tierFromText(chosen.label);
    if (tier) return tier;
  }

  return null;
}

function rolesForServiceLine(selectedLine, catalogRow) {
  const cat = catalogRow || {};
  const line = selectedLine || {};
  const blob = `${line.category || ''} ${cat.category || ''} ${line.name || ''} ${
    cat.name || ''
  } ${cat.service_type || ''}`.toLowerCase();

  const nailKeys = ['manicure', 'pedicure', 'nail ', 'nail care', 'gel polish'];
  const skinKeys = [
    'facial',
    'clean-up',
    'clean up',
    'cleanup',
    'skin care',
    'skincare',
    'wax',
    'detan',
    'bleach',
    'peel',
  ];
  const colorKeys = [
    'color',
    'colour',
    'highlight',
    'streak',
    'grey',
    'global color',
    'ammonia',
  ];
  const hairKeys = [
    'hair',
    'cut',
    'haircut',
    'blow',
    'styling',
    'shave',
    'beard',
    'spa ritual',
    'treatment',
    'keratin',
    'smoothen',
    'rebond',
  ];
  const massageKeys = ['massage', 'reflexology', 'aromatherapy', 'body spa'];

  if (nailKeys.some((k) => blob.includes(k))) {
    return new Set([...NAIL_CARE_ROLES, 'beauty', 'hair_and_beauty']);
  }
  if (massageKeys.some((k) => blob.includes(k))) {
    return new Set([...SKIN_WELLNESS_ROLES, 'therapist', 'hair_and_beauty']);
  }
  if (skinKeys.some((k) => blob.includes(k))) {
    return new Set([...SKIN_WELLNESS_ROLES, 'hair_and_beauty']);
  }
  if (colorKeys.some((k) => blob.includes(k))) {
    return new Set([...HAIR_STYLIST_TIER_ROLES, COLOR_SPECIALIST_ROLE]);
  }
  if (hairKeys.some((k) => blob.includes(k))) {
    return new Set([...HAIR_STYLIST_TIER_ROLES, COLOR_SPECIALIST_ROLE]);
  }
  return null;
}

function intersectSets(sets) {
  if (!sets.length) return null;
  let out = new Set(sets[0]);
  for (let i = 1; i < sets.length; i += 1) {
    const next = new Set();
    for (const v of out) {
      if (sets[i].has(v)) next.add(v);
    }
    out = next;
  }
  return out;
}

function maxTierRank(tierA, tierB) {
  if (!tierA) return tierB;
  if (!tierB) return tierA;
  const ra = STYLIST_TIER_RANK[tierA] || 0;
  const rb = STYLIST_TIER_RANK[tierB] || 0;
  return ra >= rb ? tierA : tierB;
}

/**
 * Combined eligibility from all selected services (one stylist serves all).
 */
export function eligibilityForSelectedServices(selectedServices, catalog) {
  const lines = Array.isArray(selectedServices) ? selectedServices : [];
  if (!lines.length) {
    return { minStylistTier: null, allowedRoles: null };
  }

  let minStylistTier = null;
  const roleSets = [];

  for (const line of lines) {
    const cat = catalogLookup(catalog, line);
    minStylistTier = maxTierRank(minStylistTier, inferRequiredStylistTier(line, cat));
    const roles = rolesForServiceLine(line, cat);
    if (roles) roleSets.push(roles);
  }

  const allowedRoles = roleSets.length ? intersectSets(roleSets) : null;
  return { minStylistTier, allowedRoles };
}

export function staffMeetsStylistTier(staffRole, minTier) {
  if (!minTier) return true;
  const role = staffRoleSelectValue(staffRole);
  const rank = STYLIST_TIER_RANK[role];
  const need = STYLIST_TIER_RANK[minTier];
  if (need == null) return true;
  if (rank == null) return true;
  return rank >= need;
}

export function staffEligibleForServices(person, selectedServices, catalog) {
  const { minStylistTier, allowedRoles } = eligibilityForSelectedServices(
    selectedServices,
    catalog,
  );
  const role = staffRoleSelectValue(person?.role);

  if (allowedRoles && allowedRoles.size > 0 && !allowedRoles.has(role)) {
    return false;
  }
  if (!staffMeetsStylistTier(role, minStylistTier)) {
    return false;
  }
  return true;
}

export function filterStaffForSelectedServices(staffList, selectedServices, catalog) {
  const list = Array.isArray(staffList) ? staffList : [];
  const lines = Array.isArray(selectedServices) ? selectedServices : [];
  if (!lines.length) return list;
  return list.filter((person) => staffEligibleForServices(person, lines, catalog));
}

export function staffFilterHint(selectedServices, catalog) {
  const { minStylistTier, allowedRoles } = eligibilityForSelectedServices(
    selectedServices,
    catalog,
  );
  if (!selectedServices?.length) return '';
  const parts = [];
  if (minStylistTier) {
    const labels = {
      style_director: 'Style Director',
      senior_stylist: 'Senior Stylist',
      stylist: 'Stylist',
    };
    parts.push(labels[minStylistTier] || minStylistTier.replace(/_/g, ' '));
  }
  if (allowedRoles && allowedRoles.size && allowedRoles.size <= 4) {
    /* optional — tier message is enough */
  }
  if (!parts.length) return 'Showing staff who can perform your selected services.';
  return `Showing stylists at ${parts[0]} level or above for your selected services.`;
}
